import { LitElement, html, css, unsafeCSS } from "./lit-all.min.js";

const sharedStyles = css`
	:host {
		font: var(--text-md);
	}

	 h1, h2, h3, h4, p, ul, select, button {
		margin: 0;
		padding: 0;
		background: none;
		border: none;
		font: inherit;
		color: inherit;
	}
`;

const themeStylesheet = [...document.styleSheets].find(sheet => sheet.href?.includes('theme.css'));
const buttonRules = [...themeStylesheet.cssRules].filter(rule => rule.cssText?.includes('.button'));
const buttonStyles = unsafeCSS(buttonRules.map(rule => rule.cssText).join('\n'));

/**
 * Reviews the merchant has hidden, listed on the product's
 * `custom.excluded_reviews` metafield. Shared by every provider so a review
 * stays hidden whichever source is answering; <product-reviews> fills it before
 * the first fetch.
 */
const excludedReviews = new Set();

class OkendoReviewsProvider {
	constructor() {
		this.userId = theme.storeLocation == 'AU' ? 'a3602b8e-9d05-4cb8-a700-0b0b0096f048' :
									theme.storeLocation == 'US' ? 'ebb9e0f0-c4de-4865-be89-2cfae6224d4b' :
									theme.storeLocation == 'UK' ? 'd2785c22-afc7-417f-a7df-011ec3caf480' :
									null;
		this.apiUrl = `https://api.okendo.io/v1/stores/${this.userId}`;
		this.fetchCache = new Map();

		this.fetchedReviews = new Map();
		this.fetchedReviewsGrouped = new Map();
		this.nextUrls = new Map();
		this.maxReviewCount = new Map();
		this.excludedReviews = excludedReviews;	}

	async #get(endpoint, { productId, groupId}) {
		const url = productId ? `${this.apiUrl}/products/shopify-${productId}/${endpoint}` : `${this.apiUrl}/collections/${groupId}/${endpoint}`;

		if (this.fetchCache.has(url)) return this.fetchCache.get(url);
		else {
			const promise = fetch(url).then(resp => resp.json());
			this.fetchCache.set(url, promise);
			return promise;
		}
	}

	async #getAggregate({ productId, groupId }) {
		const aggregateResp = await this.#get('review_aggregate', { productId, groupId });
		// A product or collection Okendo doesn't know about answers without an
		// aggregate at all, so fall back to an empty one rather than throwing
		const aggregate = aggregateResp.reviewAggregate ?? {};

		if ((aggregate.reviewCount ?? 0) < 25 && this.excludedReviews.size > 0) {
			// Manually calculate aggregate by fetching the first 25 reviews
			const reviewsResp = await this.#get('reviews?limit=25', { productId, groupId });
			const reviews = (reviewsResp.reviews ?? []).filter(r => !this.excludedReviews.has(r.reviewId));

			return {
				reviewCount: reviews.length,
				recsPercentage: reviews.length > 0 ? Math.round((reviews.filter(r => r.isRecommended).length / reviews.length) * 100) : null,
				averageRating: reviews.length > 0 ? reviews.reduce((acc, curr) => acc + curr.rating, 0) / reviews.length : null,
				attributeRatings: aggregate.attributeRatings ?? [],
			}
		}
		{
			return {
				reviewCount: aggregate.reviewCount || 0,
				recsPercentage: aggregate.ratingAndReviewCount > 0 ? Math.round((aggregate.recommendationCount / aggregate.ratingAndReviewCount) * 100) : null,
				averageRating: aggregate.ratingAndReviewCount > 0? aggregate.ratingAndReviewValuesTotal / aggregate.ratingAndReviewCount : null,
				attributeRatings: aggregate.attributeRatings ?? [],
			}
		}
	}

	async #getAggregateGrouped({ productIds }) {
		const aggregates = (await Promise.all(productIds.map(id => this.#getAggregate({ productId: id }))));
		const aggregatesWithReviews = aggregates.filter(a => a.reviewCount > 0);
		
		const reviewCount = aggregates.reduce((acc, curr) => acc + curr.reviewCount, 0);

		return {
			reviewCount: reviewCount,
			recsPercentage: Math.round(aggregatesWithReviews.reduce((acc, curr) => acc + curr.recsPercentage * curr.reviewCount, 0) / reviewCount),
			averageRating: aggregatesWithReviews.reduce((acc, curr) => acc + curr.averageRating * curr.reviewCount, 0) / reviewCount,
			attributeRatings: []
		}
	}

	async getProductAggregate(productId) {
		return this.#getAggregate({ productId });
	}

	async getProductAggregateGrouped(productIds) {
		return this.#getAggregateGrouped({ productIds });
	}

	async getGroupAggregate(groupId) {
		return this.#getAggregate({ groupId });
	}

	// Both endpoints return {} when the product has no published summary/keywords
	async getProductReviewsSummary(productId) {
		const json = await this.#get('reviews_summary', { productId });
		return json.reviewsSummary?.body ? json.reviewsSummary : null;
	}

	async getProductReviewsKeywords(productId) {
		const json = await this.#get('reviews_keywords', { productId });
		const topics = json.reviewsKeywords?.keywordTopics ?? [];

		return topics.map(({ topic, keywords }) => ({
			name: topic,
			keywords: keywords.map(keyword => keyword.name),
			occurrences: keywords.reduce((acc, curr) => acc + curr.occurrences, 0),
		}));
	}

	// A bundle has no keywords of its own, so pool the topics of its children:
	// same topic name, the union of their keywords, occurrences added up
	async getProductReviewsKeywordsGrouped(productIds) {
		const topicsPerProduct = await Promise.all(productIds.map(id => this.getProductReviewsKeywords(id)));
		const merged = new Map();

		for (const topic of topicsPerProduct.flat()) {
			const existing = merged.get(topic.name);

			if (!existing) merged.set(topic.name, { ...topic, keywords: [...topic.keywords] });
			else {
				existing.keywords = [...new Set([...existing.keywords, ...topic.keywords])];
				existing.occurrences += topic.occurrences;
			}
		}

		return [...merged.values()];
	}

	// Okendo takes one keywordTopics param per topic, listing the topic name
	// followed by every keyword it covers
	#reviewsEndpoint({ sortBy, limit, keywordTopics = [] }) {
		const params = [`limit=${limit}`];
		if (sortBy) params.push(`orderBy=${sortBy}`);

		params.push(...keywordTopics.map(topic =>
			`keywordTopics=${encodeURIComponent([topic.name, ...topic.keywords].join(','))}`
		));

		return `reviews?${params.join('&')}`;
	}

	// The same topic name can cover different keywords depending on whether it
	// came from one product or was merged across a bundle, so key on both
	#reviewsKey({ productId, groupId, sortBy, keywordTopics = [] }) {
		const topics = keywordTopics.map(topic => [topic.name, ...topic.keywords].join(',')).join('|');
		return `${productId || groupId}-${sortBy}-${topics}`;
	}

	// A filtered count only comes back alongside the reviews, so ask for the
	// smallest page we can and read the total off it
	async getProductReviewCount(productId, { keywordTopics = [] } = {}) {
		const json = await this.#get(this.#reviewsEndpoint({ limit: 1, keywordTopics }), { productId });
		return json.total?.count ?? 0;
	}

	async getProductReviewCountGrouped(productIds, { keywordTopics = [] } = {}) {
		const counts = await Promise.all(productIds.map(id => this.getProductReviewCount(id, { keywordTopics })));
		return counts.reduce((acc, curr) => acc + curr, 0);
	}

	async #getReviews({ productId, groupId, sortBy, limit, page, keywordTopics = [] }) {
		page = Math.max(1, page);

		const key = this.#reviewsKey({ productId, groupId, sortBy, keywordTopics });

		if (!this.fetchedReviews.has(key)) {
			const json = await this.#get(this.#reviewsEndpoint({ sortBy, limit, keywordTopics }), { productId, groupId });
			this.fetchedReviews.set(key, json.reviews.filter(r => !this.excludedReviews.has(r.reviewId)));
			this.nextUrls.set(key, json.nextUrl);
		}

		const fetchedReviews = this.fetchedReviews.get(key);
		let nextUrl = this.nextUrls.get(key);

		const targetLength = page * limit;
		const requiredLength = (page - 1) * limit + 1;

		if (this.maxReviewCount.has(key) && fetchedReviews.length < this.maxReviewCount.get(key)) return [];

		while (fetchedReviews.length < targetLength && nextUrl) {
			const fetchUrl = new URL(`https://api.okendo.io/v1${nextUrl}`);
			fetchUrl.searchParams.set('limit', 25);

			const resp = await fetch(fetchUrl.toString());
			const json = await resp.json();

			fetchedReviews.push(...json.reviews.filter(r => !this.excludedReviews.has(r.reviewId)));
			nextUrl = json.nextUrl;
			this.nextUrls.set(key, nextUrl);
		}

		if (fetchedReviews.length < requiredLength) {
			this.maxReviewCount.set(key, fetchedReviews.length);
			return [];
		}
		return fetchedReviews.slice(targetLength - limit, targetLength);
	}

	async #getReviewsGrouped({ productIds, sortBy, limit, page, keywordTopics = [] }) {
		page = Math.max(1, page);

		const key = this.#reviewsKey({ productId: productIds.join(','), sortBy, keywordTopics });

		if (!this.fetchedReviewsGrouped.has(key)) this.fetchedReviewsGrouped.set(key, []);

		const fetchedReviews = this.fetchedReviewsGrouped.get(key);

		const targetLength = page * limit;
		const requiredLength = (page - 1) * limit + 1;

		for (const id of productIds) {
			const reviews = await this.#getReviews({ productId: id, sortBy, limit, page, keywordTopics });
			fetchedReviews.push(...reviews.filter(r => !fetchedReviews.includes(r)));
		}

		const sortType = sortBy.split(' ')[0];
		const sortOrder = sortBy.split(' ')[1] === 'asc' ? -1 : 1;
		const sortKey = 
			sortType === 'date' ? 'dateCreated' 
		: sortType === 'rating' ? 'rating' 
		: 'helpfulCount';

		const sortKeyFunc = r => sortType == 'date' ? new Date(r.dateCreated) : r[sortKey];

		fetchedReviews.sort((a, b) => (sortKeyFunc(b) - sortKeyFunc(a)) * sortOrder);

		if (fetchedReviews.length < requiredLength) return [];
		return fetchedReviews.slice(targetLength - limit, targetLength);
	}

	async getProductReviews(productId, { sortBy = 'date desc', limit = 5, page = 1, keywordTopics = [] } = {}) {
		return this.#getReviews({ productId, sortBy, limit, page, keywordTopics });
	}

	async getProductReviewsGrouped(productIds, { sortBy = 'date desc', limit = 5, page = 1, keywordTopics = [] } = {}) {
		return this.#getReviewsGrouped({ productIds, sortBy, limit, page, keywordTopics });
	}

	async getGroupReviews(groupId, { sortBy = 'date desc', limit = 5, page = 1 } = {}) {
		return this.#getReviews({ groupId, sortBy, limit, page });
	}

	async upvote(reviewId) {
		await fetch(`${this.apiUrl}/reviews/${reviewId}/vote`, {
			method: 'POST',
			body: JSON.stringify({ voteType: 'upvote' })
		});
	}

	async upvoteCancel(reviewId) {
		await fetch(`${this.apiUrl}/reviews/${reviewId}/vote`, {
			method: 'POST',
			body: JSON.stringify({ voteType: 'upvote-cancel' })
		});
	}

	getProductReviewLink(productId) {
		return `https://reviews.okendo.io/stores/${this.userId}/review?productId=shopify-${productId}&locale=en`
	}
}

/**
 * Reads product reviews from Bed Threads' own mirror of Okendo, through the
 * `reviews` Worker (workers/reviews) instead of api.okendo.io.
 *
 * Product scope only, deliberately: collection groups, AI summaries and
 * keywords are not mirrored and are not served by the Worker, so `provider`
 * below keeps routing those to Okendo. The Worker returns Okendo-shaped review
 * objects, so nothing downstream of here can tell the two sources apart.
 *
 * Paging is the other difference worth knowing about. Okendo hands out cursors,
 * so the provider above has to walk every page to reach page N and cache what
 * it passes; the Worker takes `page` directly and reports the total, so each
 * request stands alone and a jump to the last page costs one call.
 */
class BedThreadsReviewsProvider {
	/** Only the mirrored stores. UK has no Okendo API access, so it isn't in the mirror. */
	static stores = { AU: 'au', US: 'us' };

	constructor() {
		this.store = BedThreadsReviewsProvider.stores[theme.storeLocation] ?? null;
		this.apiUrl = theme.reviewsApiUrl ?? 'https://reviews.bedthreads.com.au';
		this.fetchCache = new Map();
		this.excludedReviews = excludedReviews;
	}

	/** True when this source can answer for the current store at all. */
	get available() {
		return Boolean(this.store);
	}

	async #get(endpoint, productIds, params = {}) {
		const url = new URL(`${this.apiUrl}/${this.store}/${endpoint}`);
		url.searchParams.set('productIds', productIds.join(','));
		for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
		if (this.excludedReviews.size > 0) url.searchParams.set('exclude', [...this.excludedReviews].join(','));

		const key = url.toString();
		if (!this.fetchCache.has(key)) {
			this.fetchCache.set(key, fetch(key).then(resp => {
				if (!resp.ok) throw new Error(`Reviews API responded ${resp.status}`);
				return resp.json();
			}));
		}

		return this.fetchCache.get(key);
	}

	async #getAggregate(productIds) {
		const aggregate = await this.#get('aggregate', productIds);

		return {
			reviewCount: aggregate.reviewCount || 0,
			recsPercentage: aggregate.recsPercentage,
			averageRating: aggregate.averageRating,
			attributeRatings: aggregate.attributeRatings ?? [],
		};
	}

	async getProductAggregate(productId) {
		return this.#getAggregate([productId]);
	}

	/**
	 * One request for the whole bundle, so the count and average are computed
	 * over the combined set rather than by re-weighting per-product averages.
	 */
	async getProductAggregateGrouped(productIds) {
		return this.#getAggregate(productIds);
	}

	async #getReviews(productIds, { sortBy, limit, page }) {
		const { reviews } = await this.#get('reviews', productIds, { sort: sortBy, limit, page: Math.max(1, page) });
		return reviews;
	}

	async getProductReviews(productId, { sortBy = 'date desc', limit = 5, page = 1 } = {}) {
		return this.#getReviews([productId], { sortBy, limit, page });
	}

	/** Sorted and paged across all the products at once, by the database. */
	async getProductReviewsGrouped(productIds, { sortBy = 'date desc', limit = 5, page = 1 } = {}) {
		return this.#getReviews(productIds, { sortBy, limit, page });
	}
}

const okendoProvider = new OkendoReviewsProvider();
const bedThreadsProvider = new BedThreadsReviewsProvider();

/**
 * Builds the object a widget's components talk to. Product-scoped reads go to
 * `productProvider`; everything the mirror doesn't cover stays on Okendo:
 *
 *  - collection groups, which are keyed by Shopify collection id and aren't mirrored;
 *  - AI summaries and keyword topics, which Okendo computes and doesn't export;
 *  - the write-a-review link and helpful votes, because the mirror is read-only —
 *    votes still belong to Okendo, and a mirror-keyed id has no Okendo review to
 *    vote on, so those calls are dropped rather than sent as a guess.
 *
 * A keyword-filtered read follows its keywords back to Okendo for the same
 * reason: only 9% of mirrored reviews carry Okendo's keywords, and none of the
 * webhook rows do, so the mirror would answer with a list the filter never
 * touched. Better a slower, correct answer than a filter that quietly does
 * nothing.
 */
const keywordFiltered = options => (options?.keywordTopics?.length ?? 0) > 0;

const createProvider = productProvider => ({
	excludedReviews,
	source: productProvider === bedThreadsProvider ? 'bedthreads' : 'okendo',

	getProductAggregate: productId => productProvider.getProductAggregate(productId),
	getProductAggregateGrouped: productIds => productProvider.getProductAggregateGrouped(productIds),

	getProductReviews: (productId, options) =>
		(keywordFiltered(options) ? okendoProvider : productProvider).getProductReviews(productId, options),
	getProductReviewsGrouped: (productIds, options) =>
		(keywordFiltered(options) ? okendoProvider : productProvider).getProductReviewsGrouped(productIds, options),

	// Only ever called with keywords applied — the unfiltered count comes off the aggregate
	getProductReviewCount: (productId, options) => okendoProvider.getProductReviewCount(productId, options),
	getProductReviewCountGrouped: (productIds, options) => okendoProvider.getProductReviewCountGrouped(productIds, options),

	getProductReviewsSummary: productId => okendoProvider.getProductReviewsSummary(productId),
	getProductReviewsKeywords: productId => okendoProvider.getProductReviewsKeywords(productId),
	getProductReviewsKeywordsGrouped: productIds => okendoProvider.getProductReviewsKeywordsGrouped(productIds),

	getGroupAggregate: groupId => okendoProvider.getGroupAggregate(groupId),
	getGroupReviews: (groupId, options) => okendoProvider.getGroupReviews(groupId, options),

	getProductReviewLink: productId => okendoProvider.getProductReviewLink(productId),
	upvote: reviewId => (isOkendoReviewId(reviewId) ? okendoProvider.upvote(reviewId) : Promise.resolve()),
	upvoteCancel: reviewId => (isOkendoReviewId(reviewId) ? okendoProvider.upvoteCancel(reviewId) : Promise.resolve()),
});

/**
 * Reviews the mirror only knows from Okendo's CSV export carry no Okendo id, so
 * the Worker gives them a `key:`-prefixed id of its own. Those can't be voted on.
 */
const isOkendoReviewId = reviewId => Boolean(reviewId) && !String(reviewId).startsWith('key:');

/**
 * `settings.reviews_source` decides which widgets read from the mirror:
 * `okendo` none, `bundles` bundle widgets and colour filters, `bedthreads` all
 * of them. `?reviews_source=` overrides it so either source can be checked on a
 * live page without a deploy.
 */
const reviewsSource = new URLSearchParams(location.search).get('reviews_source') ?? theme.reviewsSource ?? 'okendo';

const providers = new Map();

/**
 * The provider for one widget, in the state it is currently in.
 *
 * The mirror earns its place wherever a list spans several products at once: a
 * bundle's children, or the colours a shopper has filtered to. Okendo has to be
 * walked one product and one cursor page at a time and merged in the browser,
 * which makes a sort a sort of whatever happened to be fetched; the Worker sorts
 * and pages the whole set in a single query and knows the real total.
 *
 * An unfiltered single product is the case Okendo already answers in one request,
 * so it stays there — and with it the summary and keywords, which the mirror
 * doesn't carry.
 */
const providerFor = ({ bundle = false, colourFiltered = false } = {}) => {
	const spansProducts = bundle || colourFiltered;
	const mirror = (reviewsSource === 'bedthreads' || (reviewsSource === 'bundles' && spansProducts)) && bedThreadsProvider.available;
	const productProvider = mirror ? bedThreadsProvider : okendoProvider;

	if (!providers.has(productProvider)) providers.set(productProvider, createProvider(productProvider));
	return providers.get(productProvider);
};

/** The default for anything that isn't a widget — including window.reviewsProvider. */
const provider = providerFor();

window.reviewsProvider = provider;

class ProductReviews extends LitElement {
	static properties = {
		heading: { type: String },
		aggregate: { type: Object },
		productName: { type: String },
		productIds: { type: Array },
		groupIds: { type: Array },
		tabNames: { type: Array },
		sortBy: { type: String },
		productId: { type: String },
		groupId: { type: String },
		defaultTab: { type: String },
		/** Set by the widget snippet on a BundleProduct; picks the review source. */
		bundle: { type: Boolean },
		provider: { type: Object },
		reviewsSummary: { type: Object },
		keywordTopics: { type: Array },
		selectedTopics: { type: Array },
		filteredCount: { type: Number },
		colours: { type: Array },
		selectedColours: { type: Array },
	}

	constructor() {
		super();

		this.bundle = false;
		this.provider = provider;

		this.productIds = [];
		this.productId = '';
		this.productName = '';
		this.listProductIds = [];

		this.groupIds = [];	
		this.groupId = '';

		this.tabNames = [];
		this.defaultTab = '';

		this.sortBy = 'date desc';

		this.aggregate = null;
		this.reviewsSummary = null;
		this.keywordTopics = [];
		this.selectedTopics = [];
		this.filteredCount = null;

		this.colours = [];
		this.selectedColours = [];

		this.heading = 'Customer Reviews';
		this.recsPercentage = 0;
		this.reviewCount = 0;
		this.averageRating = 0;
		this.attributeRatings = [];

		this.reviewCounts = {};

		this.numberFormatter = new Intl.NumberFormat();

		const excludedReviews = this.hasAttribute('excludedReviews') ? JSON.parse(this.getAttribute('excludedReviews')) : [];
		if (excludedReviews) excludedReviews.forEach(e => provider.excludedReviews.add(e));
	}

	// Selected colours are sibling products of the one on the page, so they take
	// over the scope the aggregate and the list are built from
	get scopeProductIds() {
		if (this.selectedColours.length > 0) return this.selectedColours;
		if (this.productId) return [this.productId];
		return this.productIds;
	}

	async loadAggregate() {
		// The first update runs before the default tab is picked, so there is
		// nothing to ask for yet — an empty id would only hide the widget
		if (this.productIds.length === 0 && !this.groupId) return;

		let aggregates = [];
		const ids = this.scopeProductIds;

		if (ids.length > 0) {
			// One request per colour, so the dropdown can show each one's count
			aggregates = await Promise.all(ids.map(id => this.provider.getProductAggregate(id)));
			aggregates.forEach((agg, idx) => this.reviewCounts[ids[idx]] = agg.reviewCount || 0);
		}
		else if (this.groupIds.length > 0) aggregates = [await this.provider.getGroupAggregate(this.groupId)];

		aggregates = aggregates.filter(a => a.reviewCount);
		const reviewCount = aggregates.reduce((acc, curr) => acc + curr.reviewCount, 0);

		this.aggregate = {
			reviewCount: reviewCount,
			averageRating: aggregates.reduce((acc, curr) => acc + curr.averageRating * curr.reviewCount, 0) / reviewCount,
			recsPercentage: Math.round(aggregates.reduce((acc, curr) => acc + curr.recsPercentage * curr.reviewCount, 0) / reviewCount),
			attributeRatings: aggregates.length > 0 ? aggregates[0].attributeRatings : [],
		}

		// A filter that happens to come back empty shouldn't take the widget with it
		if (this.aggregate.reviewCount == 0 && this.selectedColours.length === 0) this.hidden = true;
	}

	async loadInsights() {
		this.reviewsSummary = null;
		this.keywordTopics = [];
		if (this.selectedTopics.length > 0) this.selectedTopics = [];

		// Insights are only available per product, so group widgets have none.
		// A bundle showing every child merges their keywords, but a summary
		// only makes sense for a single product
		const ids = this.productId ? [this.productId] : this.productIds;

		if (ids.length === 0) return;

		const [summary, topics] = await Promise.all([
			ids.length === 1 ? this.provider.getProductReviewsSummary(ids[0]) : null,
			this.provider.getProductReviewsKeywordsGrouped(ids),
		]);

		this.reviewsSummary = summary;
		this.keywordTopics = topics
			.sort((a, b) => b.occurrences - a.occurrences)
			.slice(0, 6);
	}

	// The filtered count replaces the aggregate one while keywords are applied
	async loadFilteredCount() {
		if (this.selectedTopics.length === 0) {
			this.filteredCount = null;
			return;
		}

		this.filteredCount = await this.provider.getProductReviewCountGrouped(
			this.scopeProductIds,
			{ keywordTopics: this.selectedTopics }
		);
	}

	toggleTopic(topic) {
		this.selectedTopics = this.selectedTopics.includes(topic)
			? this.selectedTopics.filter(selected => selected !== topic)
			: [...this.selectedTopics, topic];
	}

	toggleColour(id) {
		this.selectedColours = this.selectedColours.includes(id)
			? this.selectedColours.filter(selected => selected !== id)
			: [...this.selectedColours, id];
	}

	clearFilters() {
		this.selectedColours = [];
		this.selectedTopics = [];
	}

	get activeFilters() {
		const colours = this.selectedColours
			.map(id => this.colours.find(colour => colour.id == id))
			.filter(Boolean)
			.map(colour => ({ name: colour.name, remove: () => this.toggleColour(colour.id) }));

		const topics = this.selectedTopics.map(topic => ({ name: topic.name, remove: () => this.toggleTopic(topic) }));

		return [...colours, ...topics];
	}

	scrollToTop() {
		const top = this.shadowRoot.querySelector('.top');
		if (top.getBoundingClientRect().top < 0) top.scrollIntoView({ behavior: 'instant' });
	}

	willUpdate(changedProperties) {
		// First, so the loads below already run against the widget's own source.
		// Filtering to colours changes it: that list spans several products, which is
		// what the mirror is for
		if (changedProperties.has('bundle') || changedProperties.has('selectedColours')) {
			this.provider = providerFor({ bundle: this.bundle, colourFiltered: this.selectedColours.length > 0 });
		}

		// Keep a stable array for the list so re-renders don't refetch it
		const listProductIds = this.scopeProductIds;
		if (listProductIds.join() !== this.listProductIds.join()) this.listProductIds = listProductIds;

		if (changedProperties.has('productIds') && this.productIds.length > 0) {
			this.loadAggregate();
			this.loadInsights();
		}

		if (changedProperties.has('productId') || changedProperties.has('groupId')) {
			this.loadAggregate();
			if (changedProperties.has('productId')) this.loadInsights();
		}

		if ((changedProperties.has('groupIds') || changedProperties.has('defaultTab')) && this.groupIds.length > 0) {
			const defaultTabIndex = this.tabNames.findIndex(name => this.defaultTab && name.includes(this.defaultTab));
			this.groupId = defaultTabIndex != -1 ? this.groupIds[defaultTabIndex] : this.groupIds[0];
			this.loadAggregate();
		}

		if (changedProperties.has('selectedColours')) {
			// Keyword topics come from Okendo and describe the product on the page, so
			// they can't survive a filter that scopes the list to other colours
			if (this.selectedColours.length > 0 && this.selectedTopics.length > 0) this.selectedTopics = [];
			this.loadAggregate();
		}

		if (changedProperties.has('selectedTopics')) this.loadFilteredCount();

		if (changedProperties.has('aggregate') && this.aggregate) {
			if (this.aggregate.reviewCount > 0) {
				this.recsPercentage = this.aggregate.recsPercentage;
				this.reviewCount = this.aggregate.reviewCount;
				this.averageRating = this.aggregate.averageRating;
				this.attributeRatings = this.aggregate.attributeRatings;
			}
		}
	}

	render() {
		let content = null;

		if (!this.aggregate) content = html`
			<h2>${this.heading}</h2>
			<slot></slot>
		`;

		else if (this.reviewCount === 0) content =  html`
			<h2>${this.heading}</h2>
			<div class="empty">
				<p>No reviews yet, write one now?</p>
				<a target="_blank" class="button button--secondary" href="${this.provider.getProductReviewLink(this.productId)}">Write a review</a>
			</div>
		`;

		else content = html`
			<h2>
				${this.heading}
				${this.productIds.length > 1 ? html`
				<div class="view">
					for 
					<reviews-dropdown 
					.options=${[
						{ value: '', name: `All ${this.productName} items` },
						...this.productIds.map((id, idx) => ({ value: id, name: this.tabNames[idx], disabled: !this.reviewCounts[id] }))
					]}
					.selected=${this.productId}
					@change=${e => this.productId = e.detail.value}
					></reviews-dropdown>
				</div>
			` : ''}
			</h2>
			${this.groupIds.length > 1 ? html`
				<ul class="tabs">
					${this.groupIds.map((id, idx) => ({ id: id, name: idx == 0 ? 'All' : this.tabNames[idx] }))
						.sort((a, b) => {
							if (this.defaultTab) {
								if (a.name.includes(this.defaultTab)) return -1;
								if (b.name.includes(this.defaultTab)) return 1;
							}
							return 0;
						})
						.map(({ id, name }) => html`
						<button class="tab ${this.groupId == id ? 'active' : ''}" @click=${() => this.groupId = id}>
							${name}
						</button>
					`)}
				</ul>
			`: ''}
			<div class="header">
				<div class="summary">
					<div class="summary-item">
						<p class="summary-text">${this.numberFormatter.format(this.reviewCount)}</review-stars></p>
						<h3 class="summary-title">Total reviews</h3>
					</div>
					<div class="summary-item">
						<p class="summary-text">${this.averageRating.toFixed(1)} <review-stars stars=${this.averageRating}></review-stars></p>
						<h3 class="summary-title">Average rating</h3>
					</div>
				</div>

				<p>${this.recsPercentage}% of users recommend ${this.scopeProductIds.length === 1 ? 'this product' : 'these products'}</p>

				${this.attributeRatings.length > 0 ? html`
					<div class="attributes">
						${this.attributeRatings.map(attr => html`
							<review-attribute-rating 
								title=${attr.title} 
								rating=${attr.value} 
								minLabel=${attr.minLabel} 
								maxLabel=${attr.maxLabel}>
							</review-attribute-rating>
						`)}
					</div>
				` : ''}
			</div>
			${this.reviewsSummary || this.keywordTopics.length > 0 ? html`
				<div class="insights">
					${this.reviewsSummary ? html`
						<div class="insight">
							<h3 class="insight-title">Reviews Summary ✨</h3>
							<p class="insight-body">${this.reviewsSummary.body}</p>
						</div>
					` : ''}
					${this.keywordTopics.length > 0 ? html`
						<div class="insight">
							<h3 class="insight-title">Customers Talked About</h3>
							<ul class="keywords">
								${this.keywordTopics.map(topic => html`
									<li>
										<button
											class="keyword"
											aria-pressed=${this.selectedTopics.includes(topic)}
											@click=${() => this.toggleTopic(topic)}
										>
											${topic.name}
										</button>
									</li>
								`)}
							</ul>
						</div>
					` : ''}
				</div>
			` : ''}
			<div class="top"></div>
			<div class="reviews-header">
				<p class="count">${this.numberFormatter.format(this.filteredCount ?? this.reviewCount)} reviews</p>
				${this.colours.length > 1 ? html`
					<reviews-colour-filter
						.colours=${this.colours}
						.selected=${this.selectedColours}
						@change=${e => this.selectedColours = e.detail.selected}
					></reviews-colour-filter>
				` : ''}
				${this.activeFilters.length > 0 ? html`
					<ul class="active-filters">
						${this.activeFilters.map(filter => html`
							<li>
								<button
									class="active-filter"
									aria-label="Remove the ${filter.name} filter"
									@click=${filter.remove}
								>
									${filter.name}
								</button>
							</li>
						`)}
						<li>
							<button class="clear-filters" @click=${this.clearFilters}>Clear all</button>
						</li>
					</ul>
				` : ''}
				<div class="sort">
					<span>Sort by:</span>
					<select class="sort" aria-label="Sort reviews by" @change=${e => this.sortBy = e.target.value}>
						<option value="date desc" ?selected=${this.sortBy === 'date desc'}>Most Recent</option>
						<option value="date asc" ?selected=${this.sortBy === 'date asc'}>Oldest</option>
						<option value="rating desc" ?selected=${this.sortBy === 'rating desc'}>Highest Rating</option>
						<option value="rating asc" ?selected=${this.sortBy === 'rating asc'}>Lowest Rating</option>
						<option value="helpful desc" ?selected=${this.sortBy === 'helpful desc'}>Most Helpful</option>
						<option value="helpful asc" ?selected=${this.sortBy === 'helpful asc'}>Least Helpful</option>
					</select>
				</div>
			</div>
		`;

		return html`
			${content}
			<product-review-list
				.provider=${this.provider}
				.productIds=${this.listProductIds}
				groupId=${this.groupId}
				sortBy=${this.sortBy}
				.keywordTopics=${this.selectedTopics}
				@changed=${this.scrollToTop}
				?hidden=${!this.aggregate || this.reviewCount === 0}
			></product-review-list>
		`;
	}

	static styles = css`
		${sharedStyles}

		${buttonStyles}

		:host {
			display: block;
		}

		:host([hidden]) {
			display: none;
		}

		h2 {
			/* Matches the H2 used by every other PDP section (WEB-3140 #13) */
			font: var(--title-sm);
			color: var(--color-text-primary);
			margin-bottom: var(--padding-sm);
			display: flex;
			flex-wrap: wrap;
			gap: 0.5ch;
			align-items: center;
		}

		.empty {
			display: flex;
			flex-direction: column;
			gap: var(--padding-sm);
			align-items: start;
		}

		.header {
			display: flex;
			flex-direction: column;
			gap: var(--padding-sm);
			margin-bottom: var(--padding-md);
		}

		.header p {
			white-space: nowrap;
		}

		.header > p {
			font: var(--text-lg);
		}

		.summary-title {
			font: var(--text-lg);
		}

		.summary {
			display: flex;
			gap: var(--padding-md);
		}

		.summary-item {
			display: flex;
			flex-direction: column;
		}

		.summary-text {
			display: flex;
			font: var(--title-xl);
			color: var(--color-text-primary);
			align-items: center;
			gap: 8px;
		}

		.attributes {
			display: flex;
			flex-wrap: wrap;
			row-gap: var(--padding-sm);
			column-gap: var(--padding-md);
		}

		.insights {
			display: flex;
			flex-direction: column;
			gap: var(--padding-lg);
			margin-bottom: var(--padding-md);
		}

		.insight {
			display: flex;
			flex-direction: column;
			gap: var(--padding-xxs);
		}

		.insight-title {
			font: var(--title-sm);
			color: var(--color-text-primary);
		}

		.insight-body {
			font: var(--text-lg);
		}

		.keywords {
			display: flex;
			flex-wrap: wrap;
			gap: var(--padding-xs);
			margin-top: var(--padding-xxs);
			list-style: none;
		}

		.keyword {
			display: flex;
			align-items: center;
			padding: var(--padding-xs) var(--padding-sm);
			border: 1px solid var(--color-border-button-primary, #BDBDBD);
			border-radius: 40px;
			font: var(--text-lg);
			color: var(--color-text-tertiary, #575757);
			cursor: pointer;
			transition: background-color 150ms, color 150ms, border-color 150ms;
		}

		@media (hover: hover) {
			.keyword:hover {
				border-color: var(--color-text-primary, #212121);
				color: var(--color-text-primary, #212121);
			}
		}

		.keyword[aria-pressed="true"] {
			background-color: var(--color-bg-inverted-primary, #212121);
			border-color: var(--color-bg-inverted-primary, #212121);
			color: var(--color-text-inverted-primary, #FFFFFF);
		}

		@media screen and (min-width: 992px) {
			.header {
				flex-direction: row;
				justify-content: space-between;
				align-items: center;
				flex-wrap: wrap;
			}

			.attributes {
				width: min-content;
			}

			.insights {
				flex-direction: row;
				gap: var(--d-64, 64px);
			}

			.insight {
				flex: 1;
				min-width: 0;
			}
		}

		@media screen and (min-width: 1100px) {
			.attributes {
				width: auto;
			}
		}
		
		.top {
			scroll-margin-top: var(--header-height);
		}

		.reviews-header {
			display: flex;
			justify-content: space-between;
			padding-block: var(--padding-xs);
			border-bottom: 1px solid var(--color-border-border-primary, #E1E1E1);
			background: white;
			position: sticky;
			top: var(--header-height);
			z-index: 5;
			flex-wrap: wrap;
			gap: var(--padding-sm);
			align-items: center;
		}

		/* An open dropdown has to clear the sticky add-to-cart bar, which shares this
		   row's offset */
		.reviews-header:has(reviews-colour-filter[open]) {
			z-index: 13;
		}

		/* The mobile sheet is fixed to the viewport, so it has to escape this row's
		   stacking context altogether or the site nav sits over its backdrop */
		@media screen and (max-width: 991px) {
			.reviews-header:has(reviews-colour-filter[open]) {
				z-index: 10001;
			}
		}

		.count {
			font: var(--text-md);
		}

		/* Sits alongside the count, with the pills taking over the gap once there
		   are any */
		reviews-colour-filter {
			margin-right: auto;
		}

		reviews-colour-filter:has(+ .active-filters) {
			margin-right: 0;
		}

		/* Matches the collection filter pills (.collection__active-filter) */
		.active-filters {
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			gap: 4px;
			list-style: none;
			margin-right: auto;
			margin-left: var(--padding-xs);
		}

		.active-filter {
			display: flex;
			align-items: center;
			gap: 5px;
			padding: 4px 8px;
			border-radius: 50px;
			background-color: #F2F2F2;
			font: var(--text-md);
			color: var(--color-text-secondary);
			cursor: pointer;
		}

		@media (hover: hover) {
			.active-filter:hover {
				background-color: #E1E1E1;
			}
		}

		.active-filter:active {
			background-color: #D1D1D1;
		}

		.active-filter::after {
			content: '';
			display: block;
			width: 9.886px;
			height: 9.886px;
			background-image: url("data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.504859 7.49511L3.99995 4.00002M7.49505 0.504922L3.99995 4.00002M3.99995 4.00002L0.504859 0.504922M3.99995 4.00002L7.49505 7.49511' stroke='%23212121' stroke-width='0.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
			background-repeat: no-repeat;
			background-position: center;
		}

		.clear-filters {
			margin-left: 8px;
			font: var(--text-md);
			color: var(--color-text-secondary);
			text-decoration: underline;
			cursor: pointer;
		}

		@media (hover: hover) {
			.clear-filters:hover {
				color: var(--color-text-primary);
			}
		}

		.reviews, .pagination {
			transition: opacity 350ms;
		}

		.sort {
			display: flex;
			align-items: center;
			gap: 4px;
			font: var(--text-md);
		}

		.view {
			display: flex;
			align-items: center;
			gap: 6px;
		}

		select {
			appearance: none;
			padding-right: 24px;
			background-image: var(--icon-arrow-down);
			background-repeat: no-repeat;
			background-position: right center;
			background-size: 24px;
		}

		select option {
			font: var(--text-md);
		}

		.tab {
		  padding: var(--padding-xs) var(--padding-sm);
			border-bottom: 1px solid var(--color-border-border-primary, #E1E1E1);
			cursor: pointer;
			white-space: nowrap;
			font: var(--text-lg);
		}

		@media (hover: hover) {
			.tab:hover {
				color: var(--color-text-primary);
			}
		}

		.tab.active {
			color: var(--color-text-primary);
  		border-bottom: 1px solid #212121;	
		}

		.tabs {
			display: flex;
			overflow-x: auto;
			margin-bottom: var(--padding-sm);
			margin-inline: -32px;
			padding-inline: 32px;
			scrollbar-width: none;
  		-ms-overflow-style: none;
		}

		.tabs::-webkit-scrollbar {
			width: 0;
			height: 0;
		}
		
		.tabs .tab:first-child {
			margin-left: auto;
		}
		
		.tabs .tab:last-child {
			margin-right: auto;
		}

		@media screen and (max-width: 512px) {
			.tabs {
				order: 5;
			}

			.reviews-header > .sort {
				width: 100%;
				justify-content: space-between;
			}

			/* Give the pills their own row rather than crowding the count */
			.active-filters {
				order: 1;
				width: 100%;
				margin-left: 0;
			}

			.reviews-header > .sort {
				order: 2;
			}
		}

		@media screen and (min-width: 992px) {
			.tabs {
				margin-bottom: var(--padding-md);
			}
		}
	`;
}

customElements.define('product-reviews', ProductReviews);


class ProductReviewList extends LitElement {
	static properties = {
		productIds: { type: Array },
		groupId: { type: String },
		sortBy: { type: String },
		keywordTopics: { type: Array },
		reviews: { type: Array },
		loaded: { type: Boolean },
		refreshing: { type: Boolean, reflect: true },
		page: { type: Number },
		pages: { type: Number },
		/** Handed down by <product-reviews>, which resolves the widget's source. */
		provider: { type: Object },
	}

	constructor() {
		super();

		this.provider = provider;

		this.productIds = [];
		this.groupId = null;
		this.reviews = [];
		this.keywordTopics = [];
		this.loaded = false;

		this.sortBy = 'date desc';
		this.limit = 5;

		this.page = 1;
		this.pages = 1;
	}

	async reviewCount() {
		// The aggregate endpoint knows nothing about keywords, so a filtered
		// count has to be totalled over the products on show
		if (this.keywordTopics.length > 0) {
			return this.provider.getProductReviewCountGrouped(this.productIds, { keywordTopics: this.keywordTopics });
		}

		const aggregate = this.groupId ? await this.provider.getGroupAggregate(this.groupId) : await this.provider.getProductAggregateGrouped(this.productIds);
		return aggregate.reviewCount;
	}

	async loadPageCount() {
		this.pages = 1;
		this.pages = Math.min(Math.ceil(await this.reviewCount() / this.limit), this.page + 50);
	}

	async loadReviews() {
		this.refreshing = true;
		this.loaded = false;
		const params =  { sortBy: this.sortBy, limit: this.limit, page: this.page, keywordTopics: this.keywordTopics };
		this.reviews = this.groupId ? await this.provider.getGroupReviews(this.groupId, params) : await this.provider.getProductReviewsGrouped(this.productIds, params);
		this.refreshing = false;
		this.loaded = true;
	}

	willUpdate(changedProperties) {
		if (changedProperties.has('provider') || changedProperties.has('sortBy') || changedProperties.has('productIds') || changedProperties.has('groupId') || changedProperties.has('keywordTopics')) {
			this.page = 1;
			this.loadPageCount();
			this.loadReviews();
		}

		if (changedProperties.has('page')) {
			this.loadReviews();
		}

		if (changedProperties.has('reviews') && changedProperties.get('reviews')?.length > 0) {
			this.dispatchEvent(new Event('changed'));
		}
	}

	async setPage(page) {
		this.page = Math.max(1, page);
	}

	render() {
		if (this.reviews.length === 0) return this.loaded
			? html`<p class="no-reviews">No reviews${this.keywordTopics.length > 0 ? ' matching those keywords' : ''}.</p>`
			: html`Loading...`;

		return html`
			<ul class="reviews">
				${this.reviews.map(review => html`
					<product-review role="listitem" .review=${review}></product-review>
				`)}
			</ul>
			${this.renderPagination()}
		`;
	}

	renderPagination() {
		if (this.pages <= 1) return;
		const pages = [];
		const visibleRange = 2;

		for (let i = this.page - visibleRange; i <= this.page + visibleRange; i++) {
			if (i < 1 || i > this.pages) continue;
			pages.push(i);
		}

		return html`
			<div class="pagination">
				${this.page != 1 ? html`<button class="previous" @click=${() => this.setPage(this.page - 1)}>Previous</button>` : ''}
				${this.page > 1 + visibleRange ? html`<button class="first" @click=${() => this.setPage(1)}>1</button><span>…</span>` : ''}
				${pages.map(page => html`
					<button class="${this.page == page ? 'active': ''}" @click=${() => this.setPage(page)} ?disabled=${this.page === page}>${page}</button>
				`)}
				${this.page < this.pages - visibleRange ? html`</button><span>…</span><button class="last" @click=${() => this.setPage(Math.min(this.pages, this.page + 50))}>${Math.min(this.pages, this.page + 50)}</button>` : ''}
				${this.page < this.pages - 1 ? html`<button class="next" @click=${() => this.setPage(this.page + 1)}>Next</button>` : ''}
			</div>
		`
	}

	static styles = css`
		${sharedStyles}
		${buttonStyles}

		:host([refreshing]) :is(.reviews, .pagination) {
			opacity: 0.3;
			pointer-events: none;
		}

		.no-reviews {
			padding-block: var(--padding-md);
		}

		.pagination {
			display: flex;
			justify-content: center;
		}

		.pagination * {
			display: flex;
			align-items: center;
			min-width: 40px;
			min-height: 36px;
			justify-content: center;
			margin-top: var(--padding-md);
			gap: var(--padding-xs);
		}

		.pagination button {
			padding-inline: var(--padding-xs);
			cursor: pointer;
		}

		.pagination button.active {
			border-radius: 2px;
			border: 1px solid var(--color-border-icon-tertiary, #767676);
		}

		.next::after, .previous::after {
			content: '';
			display: block;
			width: 24px;
			height: 24px;
			background-image: var(--icon-arrow-right);
			background-size: contain;
		}

		.previous::after {
			transform: scaleX(-1);
			order: -1;
		}
	`
}

customElements.define('product-review-list', ProductReviewList);

class ProductReview extends LitElement {
	static properties = {
		review: { type: Object },
		voting: { type: Boolean, reflect: true },
		reviewId: { type: String, reflect: true },
	}

	constructor() {
		super();

		this.review = null;
		this.reviewer;
		this.voted = false;
		this.voting = false;
	}

	willUpdate(changedProperties) {
		if (changedProperties.has('review')) {
			this.reviewer = this.review.reviewer;
			this.reviewId = this.review.reviewId;
		}
	}

	formatDate(dateString) {
		const date = Date.parse(dateString);
		const now = Date.now();
		const diff = now - date;

		const hour = 1000 * 60 * 60;
		const day = hour * 24;
		const week = day * 7;
		const month = day * 30;
		const year = day * 365;

		if (diff < hour) return 'Just now';
		else {
			let [count, unit] = '';
			
			if (diff < day) [count, unit] = [Math.floor(diff / hour), 'hour'];
			else if (diff < week) [count, unit] = [Math.floor(diff / day), 'day'];
			else if (diff < month) [count, unit] = [Math.floor(diff / week), 'week'];
			else if (diff < year) [count, unit] = [Math.floor(diff / month), 'month'];
			else [count, unit] = [Math.floor(diff / year), 'year'];

			return `${count} ${unit}${count > 1 ? 's' : ''} ago`;
		}
	}

	async vote() {
		this.voting = true;

		if (this.voted) {
			await provider.upvoteCancel(this.review.reviewId);
			this.review.helpfulCount--;
			this.voted = false;
		}
		else {
			await provider.upvote(this.review.reviewId);
			this.review.helpfulCount++;
			this.voted = true;
		}

		this.voting = false;
	}

	/**
	 * Okendo always supplies a reply avatar and a product link. The mirror carries
	 * a product handle only on backfilled reviews, so both are treated as optional.
	 */
	render() {
		if (!this.review) return html``;

		return html`
			<div class="header">
				<p class="reviewer ${this.reviewer.isVerified ? 'verified' : ''}">
					${this.reviewer.displayName} ${this.reviewer.location?.country?.name ? `from ${this.reviewer.location.country.name}` : ''}
				</p>
				<p class="recommendation ${this.review.isRecommended ? 'positive' : 'negative'}">
					${this.review.isRecommended ? 'I recommend this product' : 'I do not recommend this product'}
				</p>
			</div>
			<div class="details">
				<review-stars rating=${this.review.rating}></review-stars>
				<h3 class="title">${this.review.title}</h3>
				<p class="date">${this.formatDate(this.review.dateCreated)}</p>
			</div>
			<p class="content">${this.review.body}</p>
			${this.review.reply ? html`
				<div class="response">
					${this.review.reply.avatarUrl ? html`<img class="response-avatar" loading="lazy" src="${this.review.reply.avatarUrl}" />` : ''}
					<p class="response-date">${this.formatDate(this.review.reply.dateCreated)}</p>
					<h4 class="response-title">Response from ${this.review.reply.title}</h4>
					<div class="response-content">${document.createRange().createContextualFragment(this.review.reply.body)}</div>
				</div>
			`: ''}
			${this.review.productName?.includes('Build Your Own') ? '' : html`
				<div class="product">
					${this.review.productImageUrl ? html`<img class="product-image" src="${this.review.productImageUrl}" loading="lazy" alt="${this.review.productName}" />` : ''}
					<div class="product-details">
						<h4>Reviewing:</h4>
						<p>${this.review.productUrl
							? html`<a href="${this.review.productUrl}">${this.review.productName}</a>`
							: this.review.productName}</p>
					</div>
				</div>
			`}
			<p class="helpful">Was this review helpful? <button class="helpful-button" @click=${this.vote} ?disabled=${this.voting}>(${this.review.helpfulCount})</button></p>
		`;
	}


	static styles = css`
		${sharedStyles}

		:host {
			display: flex;
			flex-direction: column;
			gap: var(--padding-sm);
			padding-block: var(--padding-md);
			border-bottom: 1px solid var(--color-border-border-primary, #E1E1E1);
		}

		.header {
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
			gap: var(--padding-xs);
		}

		.reviewer, .recommendation {
			display: flex;
			align-items: center;
			gap: 4px;
			font: var(--text-md);
		}

		.reviewer.verified::before, .recommendation::before {
			content: '';
			display: block;
			width: 16px;
			height: 16px;
			background-size: contain;
		}

		.reviewer.verified::before {
			background-image: var(--icon-verified);
		}

		.recommendation.positive::before {
			background-image: var(--icon-thumbs-up);
		}

		.recommendation.negative::before {
			background-image: var(--icon-thumbs-down);
		}

		.details {
			display: flex;
			flex-direction: column;
			gap: 4px;
		}

		review-stars {
			margin-bottom: 4px;
		}

		.title {
			font: var(--text-lg);
			color: var(--color-text-primary);
		}

		.date {
			font: var(--text-md);
		}

		.content {
			font: var(--text-lg);
		}

		.response {
			display: grid;
			grid-template-areas: 
				"avatar header" 
				"avatar content";
			padding-left: var(--padding-xs);
			border-left: 1px solid var(--color-border-border-primary, #E1E1E1);
			row-gap: 4px;
			column-gap: 8px;
			grid-template-columns: min-content 1fr;
		}

		.response-avatar {
			grid-area: avatar;
			width: 48px;
			height: 48px;
		}

		.response-title, .response-date {
			font: var(--text-sm);
			grid-area: header;
		}

		.response-title {
			justify-self: start;
		}

		.response-date {
			justify-self: end;
		}

		.response-content {
			grid-area: content;
		}

		.response-content br {
			display: block;
			margin-top: 4px;
		}

		.product {
			display: flex;
			align-items: center;
			font: var(--text-md);
			gap: var(--padding-xs);
			align-self: start;
			position: relative;
		}

		.product a {
			color: inherit;
			text-decoration: none;
		}

		.product a::after {
			content: '';
			display: block;
			position: absolute;
			top: 0;
			left: 0;
			bottom: 0;
			right: 0;
			z-index: 5;
		}

		.product-image {
			width: 48px;
			height: auto;
			aspect-ratio: 0.8;
			background: rgba(0, 0, 0, 0.03);
		}

		.helpful {
			font: var(--text-md);
			display: flex;
			align-items: center;
			gap: var(--padding-xs);
		}

		.helpful-button {
			padding: 8px;
			margin: -8px;
			display: flex;
			align-items: center;
			gap: 4px;
			cursor: pointer;
			opacity: 0.8;
		}

		.helpful-button[disabled] {
			pointer-events: none;
			opacity: 0.5;
			transition: opacity 0.2s;
		}

		@media (hover: hover) {
			.helpful-button:hover {
				opacity: 0.9;
			}
		}

		.helpful-button:active {
			opacity: 1;
		}

		.helpful-button::before {
			content: '';
			display: block;
			width: 12px;
			height: 12px;
			background-image: var(--icon-thumbs-up);
			background-size: contain;
		}
	`;
}

customElements.define('product-review', ProductReview);


class ReviewStars extends LitElement {
	static properties = {
		rating: { type: Number },
	}

	constructor() {
		super();

		this.rating = 5;
	}

	static empty = html`
		<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M4.29369 4.11799L5.59245 1.50188C5.75917 1.16604 6.24083 1.16604 6.40755 1.50188L7.70631 4.11799L10.6107 4.54008C10.9834 4.59424 11.1319 5.04969 10.8621 5.31096L8.76087 7.34588L9.25677 10.2207C9.32046 10.5899 8.9307 10.8714 8.59727 10.6971L6 9.33899L3.40273 10.6971C3.0693 10.8714 2.67954 10.5899 2.74323 10.2207L3.23913 7.34588L1.13788 5.31096C0.868087 5.04969 1.01661 4.59424 1.38926 4.54008L4.29369 4.11799Z" stroke="#575757" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
		</svg>
	`;

	static filled = html`
		<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M4.29369 4.11799L5.59245 1.50188C5.75917 1.16604 6.24083 1.16604 6.40755 1.50188L7.70631 4.11799L10.6107 4.54008C10.9834 4.59424 11.1319 5.04969 10.8621 5.31096L8.76087 7.34588L9.25677 10.2207C9.32046 10.5899 8.9307 10.8714 8.59727 10.6971L6 9.33899L3.40273 10.6971C3.0693 10.8714 2.67954 10.5899 2.74323 10.2207L3.23913 7.34588L1.13788 5.31096C0.868087 5.04969 1.01661 4.59424 1.38926 4.54008L4.29369 4.11799Z" fill="#434343" stroke="#575757" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
		</svg>		
	`;

	static emptyStars = Array(5).fill(ReviewStars.empty);
	static filledStars = Array(5).fill(ReviewStars.filled);

	render() {
		return html`
			<div class="stars" style="--rating: ${this.rating}">
				<div class="empty">${ReviewStars.emptyStars}</div>
				<div class="filled">${ReviewStars.filledStars}</div>
			</div>
		`;
	}

	static styles = css`
		.stars {
			display: grid;
			justify-content: start;
		}

		.empty, .filled {
			display: flex;
			gap: 4px;
			grid-column: 1;
			grid-row: 1;
		}

		.filled {
			clip-path: inset(0 calc(100% - 100% * (var(--rating) / 5)) 0 0);
		}
	`;
}

customElements.define('review-stars', ReviewStars);


class ReviewAttributeRating extends LitElement {
	static properties = {
		title: { type: String },
		rating: { type: Number },
		minLabel: { type: String },
		maxLabel: { type: String }
	}

	render() {
		return html`
			<h3>${this.title}</h3>
			<span class="rating">${this.rating.toFixed(1)} out of 5</span>
			<div class="bar" aria-hidden="true" style="--progress: ${(this.rating / 5) * 100}%"></div>
			<div class="labels" aria-hidden="true">
				<span>${this.minLabel}</span>
				<span>${this.maxLabel}</span>
			</div>
		`;
	}

	static styles = css`
		${sharedStyles}

		:host {
			display: flex;
			flex-direction: column;
			gap: 4px;
			width: 100%;
			max-width: 350px;
		}

		h3 {
			font: var(--text-lg);
		}

		.rating {
			position: absolute;
			width: 0px;
			height: 0px;
			font-size: 0px;
		}

		.bar {
			height: 4px;
			background-color: var(--color-bg-secondary);
			border-radius: 2px;
			overflow: hidden;
		}

		.bar::after {
			content: '';
			display: block;
			height: 100%;
			width: var(--progress);
			background-color: var(--color-bg-inverted-tertiary, #767676);
		}

		.labels {
			font: var(--text-md);
			display: flex;
			justify-content: space-between;
		}

		.labels span:first-child {
			color: var(--color-text-disabled);
		}

		@media screen and (min-width: 992px) {
			:host {
				width: 245px;
			}
		}
	`
}

customElements.define('review-attribute-rating', ReviewAttributeRating);


class ReviewsDropdown extends LitElement {
	static properties = {
		options: { type: Array },
		selected: { type: String }
	}

	constructor() {
		super();

		this.options = [];
		this.selected = null;
	}

	willUpdate(changedProperties) {
		if (changedProperties.has('options') && this.options.length > 0 && this.selected == null) {
			this.selected = this.options[0].value;
		}
	}

	updated(changedProperties) {
		if (changedProperties.has('selected') && this.selected != null) {
			this.shadowRoot.querySelector('slide-out-modal').close();
			this.shadowRoot.querySelector('select').value = this.selected;
			this.dispatchEvent(new CustomEvent('change', { detail: { value: this.selected } }));
		}
	}

	render() {
		if (this.selected == null) return html``;

		const selectedName = this.options.find(option => option.value == this.selected).name;

		return html`
			<select aria-label="Select product to view reviews for" @change=${e => this.selected = e.target.value}>
				${this.options.map(option => html`
					<option value=${option.value} ?selected=${this.selected == option.value} ?disabled=${option.disabled}>${option.name}</option>
				`)}
			</select>
			<div>
				<button class="dropdown" aria-label="Select product to view reviews for" @click=${() => this.shadowRoot.querySelector('slide-out-modal').toggle()}>${selectedName}</button>
				<slide-out-modal desktop="dropdown" no-padding dropdown-width="max-content">
					<div class="content">
						<h3>Customer Reviews for</h3>
						${this.options.map(option => html`
							<button 
								class="option ${option.value == this.selected ? 'selected' : ''}"
								@click=${() => this.selected = option.value}
								?disabled=${option.disabled}
							>
								${option.name}
							</button>
						`)}
					</div>
				</slide-out-modal>
			</div>
		`;
	}

	static styles = css`
		${sharedStyles}

		:host {
			position: relative;
		}

		.dropdown {
			padding-block: var(--padding-xxs);
			display: flex;
			align-items: center;
			gap: 8px;
			font: var(--text-lg);
			border-bottom: 1px solid var(--color-border-button-primary, #BDBDBD);
		}

		.dropdown::after {
			content: '';
			display: block;
			width: 24px;
			height: 24px;
			background: var(--icon-arrow-down);
			transition: transform 150ms;
		}

		.modal-open .dropdown::after {
			transform: scaleY(-1);
		}

		select {
			position: absolute;
			width: 0;
			height: 0;
			overflow: hidden;
			appearance: none;
		}

		.content {
			padding: var(--padding-sm);
			padding-top: 0;
		}

		h3 {
			font: var(--title-sm);
			margin-block: var(--padding-xs);
			text-align: center;
		}

		.option {
			padding-block: var(--padding-xs);
			padding-inline: var(--padding-sm);
			display: flex;
			align-items: center;
			gap: var(--padding-xs);
			color: var(--color-text-tertiary, #575757);
			width: 100%;
		}

		.option[disabled] {
			opacity: 0.5;
			pointer-events: none;
		}

		.option.selected {
			color: var(--color-text-primary, #212121);
			pointer-events: none;
		}

		.option::before {
			content: '';
			display: block;
			width: 16px;
			height: 16px;
		}

		.option.selected::before {
			background: var(--icon-check);
		}

		@media (hover: hover) {
			.option:hover {
				background: rgba(0, 0, 0, 0.04);
			}
		}

		.option:active {
			background: rgba(0, 0, 0, 0.08);
		}

		@media (min-width: 992px) {
			.dropdown {
				font: var(--title-sm);
			}

			h3 {
				display: none;
			}

			.content {
				padding: 0;
				background: var(--color-bg-secondary, #F2F2F2);
				border-radius: var(--spacing-radious-radius-sm, 2px);
				border: 1px solid var(--color-border-border-primary, #E1E1E1);
			}
		}
	`;
}

customElements.define('reviews-dropdown', ReviewsDropdown);


// Multi-select colour filter for a product's swatch collection, laid out like the
// collection page's colour filter (WEB-3157)
class ReviewsColourFilter extends LitElement {
	static properties = {
		colours: { type: Array },
		selected: { type: Array },
		open: { type: Boolean, reflect: true },
	}

	constructor() {
		super();

		this.colours = [];
		this.selected = [];
		this.open = false;
	}

	get modal() {
		return this.shadowRoot.querySelector('slide-out-modal');
	}

	toggle(id) {
		const selected = this.selected.includes(id)
			? this.selected.filter(selectedId => selectedId !== id)
			: [...this.selected, id];

		this.dispatchEvent(new CustomEvent('change', { detail: { selected } }));
	}

	clear() {
		this.dispatchEvent(new CustomEvent('change', { detail: { selected: [] } }));
	}

	render() {
		// Shows how many colours are filtered down to, or how many there are to pick from
		const count = this.selected.length > 0 ? this.selected.length : this.colours.length;

		return html`
			<div>
				<button
					class="toggle"
					aria-expanded=${this.open}
					@click=${() => this.modal.toggle()}
				>
					Filter by Colour (${count})
				</button>
				<slide-out-modal
					name="Reviews-Colour-Filter"
					desktop="dropdown"
					no-padding
					dropdown-width="max-content"
					@modalopen=${() => this.open = true}
					@modalclose=${() => this.open = false}
				>
					<div class="panel">
						<h3>Filter by Colour</h3>
						<ul class="colours">
							${this.colours.map(colour => html`
								<li>
									<button
										class="colour"
										aria-pressed=${this.selected.includes(colour.id)}
										style="--swatch: url('${colour.image}')"
										@click=${() => this.toggle(colour.id)}
									>
										${colour.name}
									</button>
								</li>
							`)}
						</ul>
						<div class="actions">
							<button class="button button--primary" @click=${() => this.modal.close()}>Apply Filters</button>
							<button class="clear" @click=${this.clear}>Clear All Filters</button>
						</div>
					</div>
				</slide-out-modal>
			</div>
		`;
	}

	static styles = css`
		${sharedStyles}

		${buttonStyles}

		:host {
			position: relative;
		}

		.toggle {
			display: flex;
			align-items: center;
			gap: 4px;
			font: var(--text-md);
			cursor: pointer;
		}

		.toggle::after {
			content: '';
			display: block;
			width: 24px;
			height: 24px;
			background: var(--icon-arrow-down);
			transition: transform 150ms;
		}

		.modal-open .toggle::after {
			transform: scaleY(-1);
		}

		.panel {
			display: flex;
			flex-direction: column;
			max-height: 70vh;
			background: white;
		}

		h3 {
			font: var(--title-sm);
			margin-block: var(--padding-xs);
			text-align: center;
		}

		.colours {
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			column-gap: var(--padding-sm);
			padding-inline: var(--padding-sm);
			overflow-y: auto;
			list-style: none;
			min-height: 0;
		}

		.colour {
			display: flex;
			align-items: center;
			gap: 8px;
			padding-block: 8px;
			width: 100%;
			font: var(--text-md);
			color: var(--color-text-tertiary, #575757);
			cursor: pointer;
			text-align: left;
		}

		.colour::before {
			content: '';
			display: block;
			min-width: 24px;
			height: 24px;
			background-image: var(--swatch);
			background-size: 100%;
			background-position: center;
			background-repeat: no-repeat;
		}

		@media (hover: hover) {
			.colour:hover {
				color: var(--color-text-primary, #212121);
			}
		}

		.colour[aria-pressed="true"] {
			color: var(--color-text-primary, #212121);
		}

		.colour[aria-pressed="true"]::before {
			outline: 1px solid var(--color-text-primary, #212121);
			outline-offset: 1px;
		}

		.actions {
			display: flex;
			flex-direction: column;
			gap: var(--padding-xs);
			padding: var(--padding-sm);
			margin-top: var(--padding-xs);
			box-shadow: 0px -8px 24px 0px rgba(0, 0, 0, 0.12);
		}

		.actions .button {
			width: 100%;
		}

		.clear {
			padding-block: var(--padding-xs);
			font: var(--text-md);
			color: var(--color-text-secondary);
			text-decoration: underline;
			cursor: pointer;
		}

		@media screen and (min-width: 992px) {
			h3, .actions {
				display: none;
			}

			.panel {
				border: 1px solid var(--color-border-border-primary, #E1E1E1);
				border-radius: var(--spacing-radious-radius-sm, 2px);
			}

			.colours {
				grid-template-columns: repeat(4, max-content);
				column-gap: var(--padding-md);
				padding: var(--padding-sm);
			}
		}
	`;
}

customElements.define('reviews-colour-filter', ReviewsColourFilter);